/**
 * @file Landing-URL resolver shared by route guards, the magic-link
 *       verify page, the waitlist-pending poller, and the site-chrome
 *       logo `homeHref`.
 * @module @labcraft/lab/ui/lib/landing
 *
 * Mirrors lab's server-side `resolvePostAuthRedirect` — duplicated on
 * the SPA because the frontend has to make the same decision in
 * several places that the backend never sees (logo click, waitlist
 * approval-flip poll, direct visit to a guarded URL). Lives here
 * rather than in `routes/index.tsx` so non-route consumers
 * (`components/site_chrome.tsx`, `pages/*`) can import it without
 * forming a cycle through `routes → pages → components`.
 */

import type { AccountLearner } from '../../client/account.ts'

/**
 * Compute the landing URL for `learner` given a requested `next`.
 *
 * Status rules:
 *   - banned → `/?banned=1` (server-side signout should usually have
 *     fired first; this is the completeness branch).
 *   - inactive → `/account/inactive`.
 *   - active but no profile → `/account/update?next=<next>`.
 *   - active + profile complete → `next`.
 *
 * @param learner - The authenticated learner state.
 * @param next - The route the learner wanted to land on. Default `/`
 *   (the resume-first home — umbrella #927 / part #940; was `/courses`).
 */
export function resolveLandingForLearner(
  learner: AccountLearner,
  next: string = '/',
): string {
  if (learner.status === 'banned') return '/?banned=1'
  if (learner.status === 'inactive') return '/account/inactive'
  if (learner.profileCompletedAt == null) {
    return `/account/update?next=${encodeURIComponent(next)}`
  }
  return next
}
